<template>
  <div class="invoice-print-wrapper">
    <div
      ref="printArea"
      class="invoice-container fade-in"
      :style="{
        '--primary': branding.primary,
        '--secondary': branding.secondary
      }"
    >


      <!-- Branding -->
      <div class="branding">
        <img v-if="branding.logo" :src="getLogo()" class="brand-logo" />
        <h1 class="brand-title">{{ branding.title }}</h1>
        <p class="brand-meta">{{ branding.address }}</p>
        <p class="brand-meta">
          {{ branding.email }}
          <span v-if="branding.phone"> | {{ branding.phone }}</span>
        </p>
      </div>

      <div class="divider"></div>

      <!-- Invoice Info -->
      <div class="invoice-info">
        <div class="left">
          <h2 class="page-title">{{ pageTitle }}</h2>

          <p><b>Number:</b> {{ record.number }}</p>
          <p><b>Date:</b> {{ record.date }}</p>

          <p v-if="record.due_date">
            <b>Due Date:</b> {{ record.due_date }}
          </p>
        </div>

        <div class="right">
          <p><b>{{ clientTitle }}:</b> {{ record.client_name }}</p>
          <p><b>Email:</b> {{ record.client_email }}</p>
          <p><b>Phone:</b> {{ record.client_mobile }}</p>
          <p><b>Address:</b> {{ record.client_address }}</p>
        </div>
      </div>

      <div class="divider"></div>

      <!-- Items Table -->
      <table class="items-table">
        <thead>
          <tr>
            <th>Item</th>
            <th width="80">Qty</th>
            <th width="100">Rate</th>
            <th width="120">Amount</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="(item, i) in record.items" :key="i">
            <td>
              <div>{{ item.name }}</div>

              <!-- Breakdowns for invoice + estimate -->
              <div class="item breakdown" v-if="isBreakdownVisible">
                <small>Base: {{ currency(item.base_amount) }}</small>
                <small>Discount: -{{ currency(item.discount_amount) }}</small>
                <small>Tax: +{{ currency(item.tax_amount) }}</small>
                <small><b>Final: {{ currency(item.final_total) }}</b></small>
              </div>
            </td>

            <td>{{ item.qty }}</td>
            <td>{{ currency(item.rate) }}</td>

            <td :style="{ color: mainType === 'creditmemo' ? '#b91c1c' : '#111' }">
              {{ mainType === 'creditmemo' ? '-' + currency(item.final_total) : currency(item.final_total) }}
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Breakdown -->
      <div class="details-breakdown">
        <h4>Calculation Breakdown</h4>

        <table class="calc-table">

          <tr>
            <td>Subtotal:</td>
            <td>{{ currency(record.subtotal) }}</td>
          </tr>

          <tr>
            <td>Total Discount:</td>
            <td>- {{ currency(record.discount_total) }}</td>
          </tr>

          <tr v-if="record.other_amount && record.other_amount != 0">
            <td>Additional Charges:</td>
            <td>{{ currency(record.other_amount) }}</td>
          </tr>

          <tr>
            <td>Total Tax:</td>
            <td>+ {{ currency(record.tax_total) }}</td>
          </tr>

          <tr class="grand-row">
            <td><b>Grand Total:</b></td>
            <td>
              <b>{{ currency(record.total) }}</b>
            </td>
          </tr>

        </table>
      </div>

      <!-- Notes -->
      <div v-if="record.notes" class="notes">
        <h3>Notes:</h3>
        <p>{{ record.notes }}</p>
      </div>

      <!-- Print Button -->
      <div class="print-btn">
        <button @click="printPage">Download</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps,onMounted,ref } from "vue";
import { apiBaseUrl,mainBaseUrl } from "@/apis/appconst";
import { getUserById } from "@/apis/authapis";

const props = defineProps({
  record: { type: Object, required: true },
  branding: { type: Object, required: true },
  mainType: { type: String, required: true },
});

const userImg = ref("");
const branding = computed(() => props.branding);
// =======================================
// Titles based on mainType
// =======================================
const pageTitle = computed(() => ({
  invoice: "Invoice",
  estimate: "Estimate",
  purchaseorder: "Purchase Order",
  creditmemo: "Credit Memo",
}[props.mainType] || "Document"));

const clientTitle = computed(() => 
  props.mainType === "purchaseorder" ? "Vendor" : "Client"
);
const getLogo = () => {

  if (!branding.value?.logo) return "";

  let logo = branding.value.logo.trim();

  // Fix duplicate base
  if (logo.startsWith(apiBaseUrl + apiBaseUrl)) {
    logo = logo.replace(apiBaseUrl + apiBaseUrl, apiBaseUrl);
  }

  // Absolute URL
  if (logo.startsWith("http://") || logo.startsWith("https://")) {

    // If ends with "/" → not an image → ignore
    if (logo.endsWith("/")) {
      
      return userImg.value;
    }

    return apiBaseUrl+logo;
  }

  // Remove starting slash
  if (logo.startsWith("/")) {
    logo = logo.substring(1);
  }

  return apiBaseUrl + logo;
};


onMounted(async () => {
  const user = JSON.parse(sessionStorage.getItem("ldata"));
  const res = await getUserById(user.user_id);
   if (res!=null && res.avatar!=null && (res.avatar+"").length>0) {
    userImg.value = apiBaseUrl+res.avatar;
   }
   else{
    userImg.value = mainBaseUrl+"images/logo.png";
   }
}
);

// Show tax/discount breakdown only for invoice OR estimate
const isBreakdownVisible = computed(() =>
  props.mainType === "invoice" || props.mainType === "estimate"
);

// Format money
function currency(n) {
  return "$" + parseFloat(n || 0).toFixed(2);
}

function printPage() {
  window.print();
}
</script>

<style scoped>
/* --------------------------------------------------
   MAIN CONTAINER
-------------------------------------------------- */
.invoice-container {
  padding: 40px;
  max-width: 900px;
  margin: auto;
  font-family: "Inter", sans-serif;
  position: relative;
  border-radius: 14px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.06);
  border: 1px solid #eef0f3;

  --primary: #111827;
  --secondary: #6b7280;
}

/* Smooth Fade */
.fade-in {
  animation: fadeIn 0.6s ease-out;
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}

/* --------------------------------------------------
   TOP BAR
-------------------------------------------------- */
.top-bar {
  height: 8px;
  background: linear-gradient(90deg, var(--primary), var(--secondary));
  border-radius: 6px;
  margin-bottom: 30px;
}

/* --------------------------------------------------
   BRANDING / HEADER
-------------------------------------------------- */
.branding {
  text-align: center;
  margin-bottom: 30px;
}

.brand-logo {
  width: 100px;
  height: auto;
  object-fit: contain;
  margin-bottom: 8px;
}

.brand-title {
  font-size: 1.9rem;
  font-weight: 700;
  color: var(--primary);
  letter-spacing: -0.5px;
}

.brand-meta {
  color: #6b7280;
  font-size: 0.9rem;
  margin-top: 3px;
}

/* Divider */
.divider {
  height: 1px;
  background: #e5e7eb;
  margin: 30px 0;
}

/* --------------------------------------------------
   INFO BLOCKS (LEFT / RIGHT)
-------------------------------------------------- */
.invoice-info {
  display: flex;
  justify-content: space-between;
  gap: 40px;
}

.invoice-info .left p,
.invoice-info .right p {
  margin: 4px 0;
  font-size: 0.95rem;
  color: #374151;
}

.page-title {
  font-size: 1.7rem;
  font-weight: 700;
  margin-bottom: 10px;
  color: var(--primary);
}

/* --------------------------------------------------
   ITEMS TABLE (PREMIUM THEME)
-------------------------------------------------- */
.items-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  font-size: 0.95rem;
}

.items-table thead {
  background: var(--primary);
  color: #fff;
}

.items-table th {
  padding: 12px;
  text-align: left;
  font-weight: 600;
  border-right: 1px solid rgba(255, 255, 255, 0.1);
}

.items-table th:last-child {
  border-right: none;
}

.items-table td {
  padding: 12px;
  border-bottom: 1px solid #e5e7eb;
  color: #374151;
}

/* Zebra rows */
.items-table tbody tr:nth-child(odd) td {
  background: #fafafa;
}

/* Hover */
.items-table tbody tr:hover td {
  background: #f3f4f6;
  transition: background 0.2s ease;
}

/* --------------------------------------------------
   TOTAL SECTION
-------------------------------------------------- */
.total-section {
  margin-top: 24px;
  text-align: right;
  font-size: 1rem;
}

.total-section p {
  margin: 6px 0;
  font-weight: 500;
}

.grand-total {
  font-size: 1.5rem;
  font-weight: 800;
  color: var(--primary);
  margin-top: 10px;
}

/* --------------------------------------------------
   NOTES BOX
-------------------------------------------------- */
.notes {
  margin-top: 35px;
  padding: 18px;
  border-left: 4px solid var(--primary);
  background: #f9fafb;
  border-radius: 10px;
}

.notes h3 {
  margin: 0 0 5px 0;
  color: var(--primary);
}

.notes p {
  font-size: 0.95rem;
  color: #374151;
  line-height: 1.5;
}

/* --------------------------------------------------
   PRINT BUTTON
-------------------------------------------------- */
.print-btn {
  text-align: center;
  margin-top: 40px;
}

.print-btn button {
  background: var(--primary);
  color: #fff;
  padding: 12px 26px;
  border-radius: 10px;
  border: none;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  letter-spacing: 0.3px;
  transition: 0.25s ease;
  box-shadow: 0 6px 14px rgba(0,0,0,0.12);
}

.print-btn button:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 20px rgba(0,0,0,0.18);
}

/* --------------------------------------------------
   THEME BINDINGS (DYNAMIC COLORS FROM API)
-------------------------------------------------- */
.invoice-container {
  border-left: 6px solid v-bind('branding.primary');
}

.brand-title {
  color: v-bind('branding.primary');
}

.items-table thead {
  background: v-bind('branding.primary');
}

.grand-total {
  color: v-bind('branding.primary');
}

.print-btn button {
  background: v-bind('branding.primary');
}
/* --------------------------------------------------
   ITEM CALCULATION BREAKDOWN (inside item cell)
-------------------------------------------------- */
.item.breakdown {
  margin-top: 4px;
  color: #6b7280;
  font-size: 0.78rem;
  line-height: 1.25rem;
}

.item.breakdown small {
  display: block;
}

/* --------------------------------------------------
   TOTAL CALCULATION BREAKDOWN
-------------------------------------------------- */
.details-breakdown {
  margin-top: 25px;
  padding: 18px;
  background: #fafafa;
  border-radius: 10px;
  /* border-left: 4px solid var(--primary); */
}

.details-breakdown h4 {
  margin: 0 0 12px 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--primary);
}

.calc-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 6px;
}

.calc-table td {
  padding: 6px 0;
  font-size: 0.95rem;
  color: #374151;
}

.calc-table td:last-child {
  text-align: right;
  font-weight: 600;
}

.calc-table .grand-row td {
  padding-top: 12px;
  font-size: 1.15rem;
  font-weight: 800 !important;
  color: var(--primary);
}
/* --------------------------------------------------
   MOBILE RESPONSIVE PATCH
   For screens below 640px
-------------------------------------------------- */
@media (max-width: 640px) {

  .invoice-container {
    padding: 20px !important;
  }

  /* Reduce header logo size */
  .brand-logo {
    width: 70px;
  }

  /* Smaller title */
  .brand-title {
    font-size: 1.4rem;
  }

  /* Center header text */
  .branding {
    margin-bottom: 18px;
  }

  /* Stack invoice-info columns */
  .invoice-info {
    flex-direction: column !important;
    gap: 12px !important;
  }

  .invoice-info .left,
  .invoice-info .right {
    width: 100%;
  }

  .page-title {
    font-size: 1.3rem !important;
  }

  /* ITEMS TABLE RESPONSIVE */
  .items-table th,
  .items-table td {
    padding: 8px 6px !important;
    font-size: 0.85rem !important;
  }

  .items-table thead {
    font-size: 0.85rem;
  }

  /* Allow item description block to wrap cleanly */
  .item.breakdown {
    font-size: 0.75rem;
    line-height: 1.2rem;
  }

  /* Total section spacing */
  .total-section {
    font-size: 0.9rem;
  }

  .grand-total {
    font-size: 1.2rem !important;
  }

  /* Breakdown box */
  .details-breakdown {
    padding: 14px !important;
  }

  .details-breakdown h4 {
    font-size: 1rem;
  }

  .calc-table td {
    font-size: 0.85rem !important;
  }

  /* Print button */
  .print-btn button {
    width: 100%;
    padding: 12px;
    font-size: 1rem !important;
  }
}
/* ================================
   🧾 PRINT VIEW PROFESSIONAL PATCH
================================ */

/* Remove card look */
.invoice-container {
  box-shadow: none !important;
  border: none !important;
  border-left: none !important;
}

/* Top bar thinner */
.top-bar {
  height: 4px !important;
  border-radius: 2px !important;
}

/* Softer divider */
.divider {
  background: #e5e7eb !important;
  margin: 20px 0 !important;
}

/* Branding spacing */
.branding {
  margin-bottom: 20px !important;
}

/* Items table → clean grid */
.items-table {
  border: 1px solid #e5e7eb !important;
}

.items-table thead {
  background: #f3f4f6 !important;
  color: #111827 !important;
}

.items-table th {
  border-right: 1px solid #e5e7eb !important;
}

.items-table th:last-child {
  border-right: none !important;
}

.items-table td {
  border-bottom: 1px solid #e5e7eb !important;
}

/* Remove zebra + hover */
.items-table tbody tr:nth-child(odd) td,
.items-table tbody tr:hover td {
  background: transparent !important;
}

/* Calculation breakdown card */
.details-breakdown {
  background: #ffffff !important;
  border: 1px solid #e5e7eb !important;
}

/* Notes block cleaner */
.notes {
  background: #ffffff !important;
  border-left: 4px solid var(--primary) !important;
}

/* Print button flat */
.print-btn button {
  box-shadow: none !important;
}

/* Hide print button when printing */
@media print {
  .print-btn {
    display: none !important;
  }

  body {
    background: #ffffff !important;
  }
}

/* Totals emphasis */
.calc-table .grand-row td {
  border-top: 2px solid #e5e7eb !important;
}

/* Smaller breakdown text */
.item.breakdown {
  color: #6b7280 !important;
}

/* Remove animations for print */
.fade-in {
  animation: none !important;
}

</style>